/**
 * Dev-only repro for GEA-7634: a popover combobox inside the *legacy*
 * `@/gf/components/Modal`, which is what `AddPartModal` and `EditPartModal`
 * wrap `PartForm` in.
 *
 * The real case is a Primoris request's "Add part" link with
 * `assetworks_api_select_part` on, which needs an M5-connected org to open —
 * the same reason GEA-6191 needed a dev page. This one is the same geometry
 * with static parts and no GraphQL, so anyone can open it on any dev org.
 *
 * Open `/dev/legacy-modal-popover-typing` on the buyers app in dev, click
 * "Select part", and type. Correct: the list narrows to what you typed.
 * Broken: the search box takes no characters at all and the list stays at the
 * unfiltered rows — Headless UI's `Dialog` focus trap pulls focus back the
 * instant Radix hands it to the input, because the popover portalled to
 * `document.body`, outside the dialog's subtree.
 *
 * Typing is the whole test. Clicks don't need focus, so the list still opens,
 * still scrolls and still selects while the bug is shipped — which is why the
 * reporter's screenshot looks like a working picker.
 */

import Field from '@/gf/components/Field'
import Form from '@/gf/components/Form'
import Modal from '@/gf/components/Modal'
import TextArea from '@/gf/components/TextArea'
import QuantityInput from '@/gf/components/inputs/Quantity'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/gf/components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/gf/components/ui/popover'
import SelectorTrigger from '@/gf/components/ui/selector-trigger'
import Action from '@/gf/components/Action'
import { useState } from 'react'

// Shaped like M5's unfiltered first page, which is what the reporter saw: rows
// sorted by a part id that starts with punctuation, so nothing near the part
// number they wanted is on screen.
const PARTS = [
  { mpn: '" 8T -4121"', description: 'WASHER' },
  { mpn: '# PC3Z*9002*C', description: 'TANK' },
  { mpn: '#0185150XP', description: 'SWITCH, PRESSURE' },
  { mpn: '#01M-5024', description: 'SEAL KIT' },
  { mpn: '#1044784', description: 'BEARING, ROLLER' },
  { mpn: 'AF26154', description: 'FILTER, AIR PRIMARY' },
  { mpn: 'AF26427', description: 'FILTER, AIR SECONDARY' },
  { mpn: 'DDE-A9360900651', description: 'GASKET, EXHAUST MANIFOLD' },
  { mpn: 'DDE-A9361807410', description: 'TURBOCHARGER' },
  { mpn: 'FS20176', description: 'FILTER, FUEL/WATER SEPARATOR' },
  { mpn: 'LF17356', description: 'FILTER, LUBE' },
  { mpn: 'P551551', description: 'FILTER, HYDRAULIC' },
]

const PartPicker = ({ value, onChange }: { value: string; onChange: (mpn: string) => void }) => {
  const [open, setOpen] = useState(false)

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <SelectorTrigger value={value} placeholder="Select part" />
      </PopoverTrigger>
      <PopoverContent className="p-0" align="start">
        <Command shouldFilter>
          <CommandInput placeholder="Select part..." />
          <CommandList>
            <CommandEmpty>No parts found.</CommandEmpty>
            <CommandGroup>
              {PARTS.map((part) => (
                <CommandItem
                  key={part.mpn}
                  className="flex flex-col gap-y-0 items-start"
                  value={part.mpn}
                  keywords={[part.mpn, part.description]}
                  onSelect={() => {
                    onChange(part.mpn)
                    setOpen(false)
                  }}
                >
                  <span className="font-medium">{part.mpn}</span>
                  <span className="text-gray-700">{part.description}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

const LegacyModalPopoverTyping = () => {
  const [open, setOpen] = useState(true)
  const [mpn, setMpn] = useState('')
  const [description, setDescription] = useState('')
  const [quantity, setQuantity] = useState<number | null>(1)

  return (
    <div className="p-8">
      <button type="button" className="text-blue-600 underline" onClick={() => setOpen(true)}>
        Reopen the modal
      </button>

      <Modal
        title="Add Part"
        open={open}
        onClose={() => setOpen(false)}
        footerAction={<Action.P type="button">Add</Action.P>}
      >
        <Form onSubmit={() => undefined}>
          <div className="space-y-4">
            <Field label="Part Number">
              <PartPicker value={mpn} onChange={setMpn} />
            </Field>

            <Field label="Description">
              <TextArea rows={4} value={description} setValue={setDescription} />
            </Field>

            <Field label="Quantity">
              <div className="w-48">
                <QuantityInput min={1} value={quantity} setValue={setQuantity} />
              </div>
            </Field>
          </div>
        </Form>
      </Modal>
    </div>
  )
}

export default LegacyModalPopoverTyping
